<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { Application } from '@splinetool/runtime'

import type {
  ContestColor,
  ContestGameReward,
  FormattedContestGameReward,
  GameContest,
  Merchant,
} from '@/components/ContestGame/contestGame'

const props = defineProps<{
  color: ContestColor
  contestGame: GameContest
  coverImage: string
  logo: string
  merchant: Merchant
  modelValue: boolean
  reward: ContestGameReward
  rewards: FormattedContestGameReward[]
  webAppUrl: string
}>()

const emit = defineEmits<{
  (e: 'completed'): void
  (e: 'loading', value: boolean): void
}>()

const { t } = useI18n()

const isBoxLoaded = ref(false)

const canvas = ref(null)
const app = ref()
const state = reactive({
  spline: {
    scene: 'https://prod.spline.design/dyUBV3DOTxbuEX9w/scene.splinecode',
    app: null,
  },
})

const boxVariables = reactive({
  merchantName: props.merchant.name,
  subtitle: t('box.subtitle'),
  titleSize: props.merchant.name.length >= 12 ? 14 : 16,
  isStarting: false,
  isLoaded: false,
})

const handleLoader = () => {
  isBoxLoaded.value = true
  boxVariables.isLoaded = true
  app.value.setVariables({ isLoaded: true })
  emit('loading', false)
}

const play = () => {
  boxVariables.isStarting = true
  app.value.setVariables({ isStarting: true })

  setTimeout(() => {
    emit('completed')
  }, 7300)
}

onMounted(async () => {
  emit('loading', true)
  if (!canvas.value) return

  const pixelRatio = window.devicePixelRatio || 1
  const width = canvas.value.clientWidth * pixelRatio
  const height = canvas.value.clientHeight * pixelRatio
  canvas.value.width = width
  canvas.value.height = height

  app.value = new Application(canvas.value)
  await app.value.load(state.spline.scene, boxVariables)
  handleLoader()
})

watch(
  () => props.modelValue,
  () => {
    play()
  },
)

onUnmounted(() => {
  if (app.value) {
    app.value.dispose()
  }
})
</script>

<template>
  <canvas
    ref="canvas"
    class="duration-800 w-ful h-full origin-center animate-float pt-24 transition-all"
    :style="{ display: isBoxLoaded ? 'block' : 'none' }"
  />
</template>
